import { ComponentProps } from 'react';

import { Box, Typography } from '@mui/material';
import { AcademicYearTitle } from 'components/pdfStudent';
import { useAppSelector, useDataLoad } from 'hooks';
import { IconWidgetStudyResults } from 'icons/widgets';
import { PerformanceYearCard } from 'portfolio3/features/pdf/components/cards';
import PdfNoDataText from 'portfolio3/features/pdf/components/PdfNoDataText';
import { chosenAcademicYearsListSelector } from 'portfolio3/features/pdf/model/selectors';
import { IMappedAverageGrade, IMappedFinalGrades, MarkType, performanceModel } from 'portfolio3/features/performance';
import { getCurrentLearningYear, getDataLoadRequests } from 'utils';

import PdfCommonDataEntityContainer from '../dataEntityContainers/common';

import './index.scss';

type PerformanceYearCardProps = ComponentProps<typeof PerformanceYearCard>;

const mapYearPerformanceToCard = (yearPerformance: IMappedFinalGrades): PerformanceYearCardProps | null => {
  const { averageGrade, subjects } = yearPerformance;

  const mappedSubjects = subjects.map((subject): PerformanceYearCardProps['subjects'][number] => {
    return {
      name: subject.subjectName,
      result: subject.markValue,
      nonMarkValue: subject.nonMarkValue,
    };
  });

  if (mappedSubjects.length === 0) return null;

  return {
    markType: MarkType.FINAL,
    averageResult: averageGrade,
    subjects: mappedSubjects,
  };
};
const mapAverageYearPerformanceToCard = (yearPerformance: IMappedAverageGrade): PerformanceYearCardProps | null => {
  const { averageMarkFive, subjectsPerformance } = yearPerformance;

  const mappedSubjects = subjectsPerformance.map((subject) => {
    return {
      name: subject.subjectName,
      result: subject.markValue,
    };
  });

  return {
    markType: MarkType.AVERAGE,
    averageResult: averageMarkFive,
    subjects: mappedSubjects,
  };
};

const PdfPerformanceContainer = () => {
  const academicYears = useAppSelector(chosenAcademicYearsListSelector);
  const averagePerformance = useAppSelector(performanceModel.selectors.learningYearAveragePerformanceSelector);
  const finalPerformance = useAppSelector(performanceModel.selectors.learningYearFinalPerformanceSelector);

  const averageYears = [getCurrentLearningYear()];
  const finalYears = finalPerformance.content.map((performance) => performance.year);

  // Оставляем только результаты для лет, выбранных в комбике "Учебный год"
  const learningYears = Array.from(new Set([...averageYears, ...finalYears])).filter((learningYear) =>
    academicYears.includes(learningYear),
  );

  useDataLoad({ shouldLoad: true, requests: getDataLoadRequests().performanceEntryMethods });

  const currentLearningYear = getCurrentLearningYear();

  return (
    <PdfCommonDataEntityContainer title="Предметные результаты" Icon={IconWidgetStudyResults}>
      {learningYears.map((learningYear) => {
        const currentYearAveragePerformance = averagePerformance.content;
        const currentYearFinalPerformance = finalPerformance.content.find(
          (performance) => performance.year === learningYear,
        );

        const averagePerformanceCardProps = currentYearAveragePerformance
          ? mapAverageYearPerformanceToCard(currentYearAveragePerformance)
          : null;
        const finalPerformanceCardProps = currentYearFinalPerformance
          ? mapYearPerformanceToCard(currentYearFinalPerformance)
          : null;

        const isEmpty =
          (!averagePerformanceCardProps || averagePerformanceCardProps?.subjects.length === 0) &&
          (!finalPerformanceCardProps || finalPerformanceCardProps?.subjects.length === 0);

        if (isEmpty) {
          return null;
        }

        return (
          <>
            <AcademicYearTitle academicYear={learningYear} />

            <Box
              key={learningYear}
              className="pdf-performance-block"
              sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
            >
              {/* средняя, отображается только для текущего года */}
              {learningYear === currentLearningYear &&
                averagePerformanceCardProps &&
                averagePerformanceCardProps?.subjects.length > 0 && (
                  <Box>
                    <Typography variant="Headings/H5" marginBottom="16px">
                      Средняя успеваемость за {learningYear} учебный год
                    </Typography>
                    <PerformanceYearCard {...averagePerformanceCardProps} />
                  </Box>
                )}
              {/* итоговая */}
              {finalPerformanceCardProps && finalPerformanceCardProps?.subjects?.length > 0 && (
                <Box>
                  <Typography variant="Headings/H5" marginBottom="16px">
                    Итоговая успеваемость за {learningYear} учебный год
                  </Typography>
                  <PerformanceYearCard {...finalPerformanceCardProps} />
                </Box>
              )}
            </Box>
          </>
        );
      })}
      {learningYears.length === 0 && <PdfNoDataText />}
    </PdfCommonDataEntityContainer>
  );
};

export default PdfPerformanceContainer;
